import React, { useEffect, useMemo, useState } from "react";
import {
  FaArrowLeft,
  FaArrowRight,
  FaCameraRetro,
  FaImages,
  FaPause,
  FaPlay,
} from "react-icons/fa";

import "./Diaporama.css";

import Reveal from "../../../components/Reveal/Reveal";
import { getAllGalleries } from "../../../services/gallerieServices";
import { getSettings } from "../../../services/settingsServices";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3317";
const SLIDE_DURATION = 5500;

const isExternalURL = (url) => {
  try {
    const newUrl = new URL(url);
    return newUrl.protocol === "http:" || newUrl.protocol === "https:";
  } catch (error) {
    return false;
  }
};

const getImageUrl = (url) => {
  if (!url) return "";

  const cleanUrl = String(url).trim();

  if (!cleanUrl) return "";

  if (
    isExternalURL(cleanUrl) ||
    cleanUrl.startsWith("data:") ||
    cleanUrl.startsWith("blob:")
  ) {
    return cleanUrl;
  }

  const cleanBaseUrl = BASE_URL.replace(/\/$/, "");

  if (cleanUrl.startsWith("/")) {
    return `${cleanBaseUrl}${cleanUrl}`;
  }

  return `${cleanBaseUrl}/${cleanUrl}`;
};

const isActivePhoto = (photo) => {
  if (photo?.is_active === undefined || photo?.is_active === null) {
    return true;
  }

  return (
    photo.is_active === true ||
    photo.is_active === 1 ||
    photo.is_active === "1"
  );
};

export default function Diaporama() {
  const [photos, setPhotos] = useState([]);
  const [galleryName, setGalleryName] = useState("La vie du club");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const fetchGalleryPhotos = async () => {
      try {
        const settingsResponse = await getSettings();
        const settings = Array.isArray(settingsResponse)
          ? settingsResponse[0]
          : settingsResponse;

        const carrouselId = settings?.carrousel_id;

        if (!carrouselId) {
          if (mounted) {
            setPhotos([]);
          }
          return;
        }

        const galleries = await getAllGalleries();

        const selectedGallery = galleries.find(
          (gallery) => Number(gallery.id) === Number(carrouselId)
        );

        if (selectedGallery?.photos?.length) {
          const activePhotos = selectedGallery.photos
            .filter(isActivePhoto)
            .map((photo) => ({
              ...photo,
              imageUrl: getImageUrl(photo.photo_url),
            }))
            .filter((photo) => photo.imageUrl);

          if (mounted) {
            setGalleryName(
              selectedGallery.nom_galerie ||
                selectedGallery.title ||
                selectedGallery.name ||
                "La vie du club"
            );
            setPhotos(activePhotos);
            setCurrentIndex(0);
          }
        }
      } catch (error) {
        console.error("Erreur lors de la récupération des photos :", error);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchGalleryPhotos();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (photos.length <= 1 || paused) return undefined;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % photos.length);
    }, SLIDE_DURATION);

    return () => clearInterval(interval);
  }, [photos.length, paused, currentIndex]);

  const currentPhoto = useMemo(() => {
    return photos[currentIndex] || null;
  }, [photos, currentIndex]);

  const goToPrevious = () => {
    setCurrentIndex((prev) => {
      if (!photos.length) return 0;
      return prev === 0 ? photos.length - 1 : prev - 1;
    });
  };

  const goToNext = () => {
    setCurrentIndex((prev) => {
      if (!photos.length) return 0;
      return (prev + 1) % photos.length;
    });
  };

  if (loading) {
    return (
      <section className="relative overflow-hidden bg-slate-950 py-20 text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="h-[480px] animate-pulse rounded-[2.5rem] bg-white/10" />
        </div>
      </section>
    );
  }

  if (!photos.length) {
    return (
      <section className="relative overflow-hidden bg-slate-950 py-20 text-white">
        <div className="mx-auto max-w-7xl px-4 text-center sm:px-6 lg:px-8">
          <div className="mx-auto max-w-xl rounded-[2rem] border border-white/10 bg-white/10 p-8 backdrop-blur-xl">
            <FaImages className="mx-auto mb-4 text-3xl text-primary" />
            <h2 className="font-barlow text-3xl font-bold">
              Aucune photo disponible
            </h2>
            <p className="mt-3 text-sm leading-6 text-white/70">
              Ajoutez des photos actives dans la galerie sélectionnée pour
              afficher ce diaporama sur la page d’accueil.
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="relative overflow-hidden bg-slate-950 py-20 text-white lg:py-28">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(242,113,49,0.22),transparent_32%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.20),transparent_34%)]" />
      <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.07)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.07)_1px,transparent_1px)] bg-[size:42px_42px] opacity-20" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="mb-10 flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-white/70 backdrop-blur-xl">
              <FaCameraRetro className="text-primary" />
              Galerie du club
            </div>

            <h2 className="mt-5 font-barlow text-4xl font-bold tracking-tight text-white sm:text-5xl">
              Moments forts du club
            </h2>

            <p className="mt-4 max-w-2xl text-base leading-8 text-white/70">
              Photos, souvenirs, entraînements, événements et instants de vie
              qui donnent envie de rejoindre l’association.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <div className="rounded-full border border-white/10 bg-white/10 px-5 py-3 text-sm font-bold text-white/80 backdrop-blur-xl">
              {currentIndex + 1} / {photos.length}
            </div>

            <button
              type="button"
              onClick={() => setPaused((prev) => !prev)}
              className="flex h-12 w-12 items-center justify-center rounded-full border border-white/10 bg-white/10 text-white/80 backdrop-blur-xl transition hover:bg-white/20"
              aria-label={paused ? "Relancer le diaporama" : "Mettre en pause"}
            >
              {paused ? <FaPlay /> : <FaPause />}
            </button>
          </div>
        </Reveal>

        <Reveal variant="zoom">
          <div
            className="relative overflow-hidden rounded-[2.5rem] border border-white/10 bg-white/10 p-2 shadow-2xl backdrop-blur-xl"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
          >
            <div className="relative h-[420px] overflow-hidden rounded-[2rem] sm:h-[560px] lg:h-[680px]">
              {photos.map((photo, index) => (
                <div
                  key={photo.id || photo.photo_url || index}
                  className={`diaporama-slide ${
                    index === currentIndex ? "is-active" : ""
                  }`}
                >
                  <img
                    src={photo.imageUrl}
                    alt={photo.description || galleryName || "Photo du club"}
                    className="diaporama-image h-full w-full object-cover"
                    loading={index === 0 ? "eager" : "lazy"}
                    draggable="false"
                    onError={(event) => {
                      event.currentTarget.style.display = "none";
                    }}
                  />
                </div>
              ))}

              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-slate-950/85 via-slate-950/20 to-transparent" />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-slate-950/50 via-transparent to-slate-950/20" />

              <button
                type="button"
                onClick={goToPrevious}
                className="absolute left-4 top-1/2 z-30 hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 bg-white/15 text-white shadow-xl backdrop-blur-xl transition hover:scale-105 hover:bg-white/25 md:flex"
                aria-label="Photo précédente"
              >
                <FaArrowLeft />
              </button>

              <button
                type="button"
                onClick={goToNext}
                className="absolute right-4 top-1/2 z-30 hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 bg-white/15 text-white shadow-xl backdrop-blur-xl transition hover:scale-105 hover:bg-white/25 md:flex"
                aria-label="Photo suivante"
              >
                <FaArrowRight />
              </button>

              <div className="absolute bottom-0 left-0 right-0 z-20 p-5 text-left sm:p-8 lg:p-10">
                <div className="max-w-2xl">
                  <p className="mb-3 inline-flex rounded-full border border-white/15 bg-white/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-white/75 backdrop-blur-xl">
                    {galleryName || "Galerie"}
                  </p>

                  <h3 className="font-barlow text-3xl font-bold leading-tight text-white drop-shadow-lg sm:text-4xl lg:text-5xl">
                    {currentPhoto?.description || "La vie du club en images"}
                  </h3>

                  <p className="mt-4 max-w-xl text-sm leading-7 text-white/70 sm:text-base">
                    Découvrez les temps forts, les événements et les moments
                    partagés au sein de l’association.
                  </p>
                </div>
              </div>

              {photos.length > 1 && !paused && (
                <div
                  key={currentIndex}
                  className="diaporama-progress absolute bottom-0 left-0 right-0 z-30 h-1 overflow-hidden bg-white/15"
                >
                  <div className="h-full bg-primary" />
                </div>
              )}
            </div>

            {photos.length > 1 && (
              <div className="mt-4 flex flex-wrap justify-center gap-2 px-4 pb-3">
                {photos.map((photo, index) => (
                  <button
                    key={photo.id || photo.photo_url || index}
                    type="button"
                    onClick={() => setCurrentIndex(index)}
                    className={`h-2.5 rounded-full transition-all ${
                      index === currentIndex
                        ? "w-8 bg-primary"
                        : "w-2.5 bg-white/35 hover:bg-white/60"
                    }`}
                    aria-label={`Afficher la photo ${index + 1}`}
                  />
                ))}
              </div>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}